import AnalysisAndCommunication from "@components/Dashboard/AnalysisAndCommunication";
import Consolidation from "@components/Dashboard/Consolidation";
import ReviewByEntities from "@components/Dashboard/Consolidation/ReviewByEntities";
import { use } from "react";
import { data, Navigate, useLoaderData } from "react-router";

import { FeaturesContext } from "@/contexts";
import type {
  ConsolidatedReview as TConsolidatedReview,
  TTrackingCampaign,
} from "@/types";

export async function loader() {
  const consolidatedReviewResponse = await fetch(
    "/api/client/reviews/consolidated?multientity=true"
  );

  if (consolidatedReviewResponse.status !== 200) {
    throw data("Failed to load consolidated review", {
      status: consolidatedReviewResponse.status,
    });
  }

  const { data: consolidatedReview } =
    (await consolidatedReviewResponse.json()) as {
      data: TConsolidatedReview | null;
    };

  // Only the parent company's campaigns are needed, their review is sent to the consolidated PDF endpoints to get the subsidiaries informations
  const trackingCampaignsResponse = await fetch(
    "/api/client/tracking_campaigns"
  );

  if (trackingCampaignsResponse.status !== 200) {
    throw data("Failed to load the tracking campaigns", {
      status: trackingCampaignsResponse.status,
    });
  }

  const { data: trackingCampaigns } =
    (await trackingCampaignsResponse.json()) as {
      data: TTrackingCampaign[];
    };

  return {
    consolidatedReview,
    trackingCampaigns: trackingCampaigns.sort(
      (a, b) =>
        new Date(a.period_start).getTime() - new Date(b.period_start).getTime()
    ),
  };
}

function DashboardMultientity() {
  const { featureFlags } = use(FeaturesContext);
  const { consolidatedReview, trackingCampaigns } =
    useLoaderData<typeof loader>();

  if (!featureFlags?.["multientity_dashboard"]) {
    return <Navigate to="/dashboard/overview" replace />;
  }

  const isConsolidated = (consolidatedReview?.subsidiaries?.length ?? 0) >= 2;

  if (!consolidatedReview || !isConsolidated) {
    return null;
  }

  return (
    <div className="gap-y-md flex flex-col">
      <Consolidation
        consolidatedReview={consolidatedReview}
        isMultientityDashboard
        deliverables={
          <AnalysisAndCommunication
            trackingCampaigns={trackingCampaigns}
            periodStart={consolidatedReview.period_start}
            periodEnd={consolidatedReview.period_end}
            companyName={consolidatedReview.company_name}
          />
        }
      />
      {consolidatedReview.subsidiaries && (
        <ReviewByEntities
          subsidiaries={consolidatedReview.subsidiaries}
          categories={consolidatedReview.review_categories}
          periodStart={consolidatedReview.period_start}
          periodEnd={consolidatedReview.period_end}
        />
      )}
    </div>
  );
}

export default DashboardMultientity;
